import { Inject, Injectable } from '@angular/core';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import OktaAuth, {
  AccessToken,
  AuthState,
  CustomUserClaims,
  IDToken,
  UserClaims,
} from '@okta/okta-auth-js';
import { ENV_CONFIG } from '@sc/data-access-environment';
import { AuthHttpService } from '@sc/data-access-http-services';
import {
  Environment,
  Fitter,
  FitterAccessType,
  CtaAction,
} from '@sc/data-access-models';
import { StorageService } from '@sc/data-access-storage';
import { DialogService } from '@sc/feature-dialog';
import {
  combineLatest,
  delayWhen,
  filter,
  from,
  lastValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  takeLast,
  tap,
  timeout,
} from 'rxjs';
import { AuthConfig } from '../../types/auth-config';
import { AUTH_MODULE_CONFIG } from '../auth-module-config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private oldAuthState: AuthState = null;
  private readonly currentUser: Observable<UserClaims<CustomUserClaims>>;

  private isOktaFlow = false;

  private isShowErrorDialogOpened = false;

  private readonly tokenStorage = sessionStorage;

  constructor(
    @Inject(ENV_CONFIG) protected env: Environment,
    @Inject(AUTH_MODULE_CONFIG) private readonly authConfig: AuthConfig,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly oktaAuthStateService: OktaAuthStateService,
    private readonly storage: StorageService,
    private readonly dialogService: DialogService,
    private readonly authHttpService: AuthHttpService
  ) {
    this.tokenStorage =
      env?.auth?.tokenManager?.storage === 'sessionStorage'
        ? sessionStorage
        : localStorage;

    // Update current user every time the token changes (this may not work on forced connexions without okta flow)
    this.currentUser = this.oktaAuthStateService.authState$.pipe(
      filter(
        (newAuthState) =>
          this.oldAuthState?.accessToken?.accessToken !==
            newAuthState?.accessToken?.accessToken || !this.oldAuthState
      ),
      switchMap((newAuthState) => {
        this.oldAuthState = newAuthState;
        if (!newAuthState?.accessToken) {
          return of<UserClaims<CustomUserClaims>>(null);
        } else {
          return from(this.oktaAuth.getUser());
        }
      }),
      shareReplay(1)
    );

    this.currentUser.subscribe((user) => {
      this.setAccountLogin(
        user?.email || this.oldAuthState?.accessToken?.claims?.sub
      );
      this.setAccountFirstName(
        user?.given_name ||
          (this.oldAuthState?.accessToken?.claims?.firstName as string)
      );
      this.setAccountLastName(
        user?.family_name ||
          (this.oldAuthState?.accessToken?.claims?.lastName as string)
      );
    });
  }

  public setOktaFlow(oktaFlow = true) {
    this.isOktaFlow = oktaFlow;
  }

  public getOktaFlow() {
    return this.isOktaFlow;
  }

  //#region Login Infos
  public login() {
    // Redirects to the okta sign in page to have okta auth flow
    return this.oktaAuth.signInWithRedirect({
      originalUri: this.authConfig.redirectAfterLogin,
    });
  }

  public async logout() {
    const wasOktaFlow = this.isOktaFlow;
    this.setOktaFlow(false);

    // If we logged-in through okta flow, we log out also through okta flow
    if (wasOktaFlow) {
      // Else, we forcefully revoke and remove the tokens from okta storage
      try {
        await Promise.all([
          this.oktaAuth.revokeAccessToken(),
          this.oktaAuth.revokeRefreshToken(),
        ]);

        this.oktaAuth.clearStorage();
        this.clearLocalStorage();
      } catch (error) {
        console.error('Error during force logout :', error);
        return false;
      }
    } else {
      return await this.oktaAuth.signOut({
        postLogoutRedirectUri: this.authConfig.redirectAfterLogout,
      });
    }
  }

  clearLocalStorage() {
    this.storage.removeItem('account-login', sessionStorage);
    this.storage.removeItem('account-first-name', sessionStorage);
    this.storage.removeItem('account-last-name', sessionStorage);
    this.storage.removeItem('okta-token-storage', localStorage);
    this.storage.removeItem('expired-access-token-period', this.tokenStorage);
    this.storage.removeItem('refresh-token', localStorage);
    this.storage.removeItem('user-types', sessionStorage);
  }

  public getAuthState$() {
    return this.oktaAuthStateService.authState$;
  }

  public async isAuthenticated() {
    return lastValueFrom(this.isAuthenticated$());
  }

  public isAuthenticated$() {
    return from(this.oktaAuth.isAuthenticated()).pipe(
      switchMap((isOktaAuth) =>
        this.getUserGroups$().pipe(
          map((userGroups) => isOktaAuth && !!userGroups?.length),
          take(1)
        )
      )
    );
  }

  notifyAndLogout() {
    if (!this.isShowErrorDialogOpened) {
      this.isShowErrorDialogOpened = true;
      this.dialogService
        .showError(
          'GlobalErrors.unauthorized',
          'GlobalErrors.unauthorizedDialogBody'
        )
        .subscribe({
          next: async () => {
            this.isShowErrorDialogOpened = false;
            await this.logout();
          },
          error: async () => {
            await this.logout();
          },
        });
    }
  }
  //#endregion

  //#region Token Infos
  public getHttpAuthorizationHeader() {
    return `Bearer ${this.oktaAuth.getAccessToken()}`;
  }

  public getAccessToken() {
    return this.oktaAuth.getAccessToken();
  }

  // This function allow to wait for the next currentUser with a timeout of 1 second
  public updateAuthState() {
    return from(this.oktaAuth.authStateManager.updateAuthState()).pipe(
      switchMap(() =>
        this.currentUser.pipe(
          take(2),
          takeLast(1),
          timeout({
            first: 1000,
            with: () => this.currentUser,
          })
        )
      )
    );
  }

  /**
    @deprecated old way of identification, to be removed eventually
  */
  public forceStoreLoginTokens(
    accessToken: AccessToken,
    idToken: IDToken,
    refreshToken: string
  ) {
    this.oktaAuth.tokenManager.setTokens({
      accessToken,
      idToken,
      refreshToken: {
        refreshToken: refreshToken,
        issuer: idToken.issuer,
        authorizeUrl: idToken.authorizeUrl,
        scopes: idToken.scopes,
        tokenUrl: idToken.issuer + '/v1/token',
        expiresAt: idToken.expiresAt,
      },
    });

    return this.updateAuthState();
  }

  /**
    @deprecated old way of refreshing tokens, to be removed eventually
  */
  public forceRefreshTokens() {
    if (!this.isOktaFlow) {
      return this.authHttpService
        .refreshAccessToken(this.oktaAuth.getRefreshToken())
        .pipe(
          tap({
            error: (err) => {
              console.error(err);
              this.oktaAuth.revokeRefreshToken();
              this.removeRefreshToken();
            },
          }),
          delayWhen((refreshResponse) =>
            this.forceStoreLoginTokens(
              refreshResponse.accessToken,
              refreshResponse.idToken,
              refreshResponse.refreshToken
            )
          )
        );
    }
    return of(true);
  }

  public getExiprationUnixMillis$() {
    return this.oktaAuthStateService.authState$.pipe(
      map((newAuthState) => newAuthState.accessToken?.expiresAt * 1000)
    );
  }

  removeRefreshToken() {
    return this.storage.removeItem('refresh-token', this.tokenStorage);
  }

  public setEncryptedInfo(encryptedInfo: string): void {
    this.storage.setItem('encrypted-info', encryptedInfo, this.tokenStorage);
  }

  public getEncryptedInfo(): string {
    return this.storage.getItem('encrypted-info', this.tokenStorage);
  }
  //#endregion

  //#region User Infos
  public getUserGroups$(): Observable<string[]> {
    return this.currentUser.pipe(
      map(
        (user) =>
          ((user?.userType ||
            this.oldAuthState?.idToken?.claims?.userType) as string[]) || []
      )
    );
  }

  public getUserFirstName$() {
    return this.currentUser.pipe(
      map(
        (user) =>
          user?.given_name || this.oldAuthState?.accessToken?.claims?.firstName
      )
    );
  }

  public getUserLastName$() {
    return this.currentUser.pipe(
      map(
        (user) =>
          user?.family_name || this.oldAuthState?.accessToken?.claims?.lastName
      )
    );
  }

  public getUserLogin$() {
    return this.currentUser.pipe(
      map((user) => user?.email || this.oldAuthState?.accessToken?.claims?.sub)
    );
  }

  public isStringUserLogin$(loginToVerify: string) {
    return this.getUserLogin$().pipe(
      map(
        (userLogin) =>
          loginToVerify?.toLowerCase().trim() ===
          userLogin?.toLowerCase().trim()
      )
    );
  }

  public userIsGroup$(group: string) {
    return this.getUserGroups$().pipe(
      map((groups) => !!groups?.includes(group))
    );
  }

  public userIsSuperAdmin$() {
    return this.userIsGroup$('grpSuperAdmin');
  }

  public userIsInsurer$() {
    return this.userIsGroup$('grpInsurer');
  }

  public userIsInsurerX$(insurerCode: string) {
    return this.userIsGroup$(`grpInsurer_${insurerCode}`);
  }

  public userIsInsurerFromCallCenter$() {
    return this.userIsGroup$('grpInsurerFromCallCenter');
  }

  public userIsBrand$() {
    return this.userIsGroup$('grpBrand');
  }

  public userIsFitterB2B$() {
    return this.userIsGroup$('grpFitterB2B');
  }

  public getUserBrandX$() {
    return this.getUserGroups$().pipe(
      map(
        (userGroups) =>
          userGroups
            .find((group) => group.includes('grpBrand_'))
            ?.split('_')[1] || null
      )
    );
  }

  public getFitterB2BBrand$() {
    return this.getUserGroups$().pipe(
      map(
        (userGroups) =>
          userGroups
            .find((group) => group.includes('grpFitterB2B_'))
            ?.split('_')[1] || null
      )
    );
  }

  public userIsFitter$() {
    return this.userIsGroup$('grpFitter');
  }

  public userIsFitterX$(insurerCode: string) {
    return this.userIsGroup$(`grpFitter_${insurerCode}`);
  }

  userHasFitterPlanningOktaAccess$(configId: string, acceptAdmin = false) {
    return (
      this.userIsFitterX$(configId) ||
      (acceptAdmin &&
        (this.userIsFitterSuperAdmin$() || this.userIsFitterAdmin$()))
    );
  }

  userHasFitterPlanningAccess$(configId: string, fitters: Fitter[] = []) {
    return combineLatest([
      this.userIsFitterSuperAdmin$(),
      this.userIsFitterAdmin$(),
      this.userHasFitterPlanningOktaAccess$(configId),
      this.userIsManagerWithGroup$(
        fitters,
        `grpFitter_${configId}` as FitterAccessType,
        true
      ),
    ]).pipe(
      map(
        ([
          isFitterSuperAdmin,
          isFitterAdmin,
          hasFitterPlanningAcces,
          isManagerWithGroups,
        ]) => {
          if (isFitterAdmin || isFitterSuperAdmin) {
            return true;
          } else return hasFitterPlanningAcces && isManagerWithGroups;
        }
      )
    );
  }

  userHasFitterSettingsOktaAccess$(acceptAdmin = false) {
    return (
      this.userIsGroup$('grpFitterParam') ||
      (acceptAdmin &&
        (this.userIsFitterSuperAdmin$() || this.userIsFitterAdmin$()))
    );
  }

  userHasFitterSettingsAccess$(fitters: Fitter[] = []) {
    return combineLatest([
      this.userIsFitterSuperAdmin$(),
      this.userIsFitterAdmin$(),
      this.userHasFitterSettingsOktaAccess$(),
      this.userIsManagerWithGroup$(fitters, 'grpFitterParam', true),
    ]).pipe(
      map(
        ([
          isFitterSuperAdmin,
          isFitterAdmin,
          hasFitterSettingsAcces,
          isManagerWithGroups,
        ]) => {
          if (isFitterAdmin || isFitterSuperAdmin) {
            return true;
          } else return hasFitterSettingsAcces && isManagerWithGroups;
        }
      )
    );
  }

  userHasFitterSearchOktaAccess$(acceptAdmin = false) {
    return (
      this.userIsGroup$('grpFitterSearch') ||
      (acceptAdmin &&
        (this.userIsFitterSuperAdmin$() || this.userIsFitterAdmin$()))
    );
  }

  userHasFitterSearchAccess$(fitters: Fitter[] = []) {
    return combineLatest([
      this.userIsFitterSuperAdmin$(),
      this.userIsFitterAdmin$(),
      this.userHasFitterSearchOktaAccess$(),
      this.userIsManagerWithGroup$(fitters, 'grpFitterSearch', true),
    ]).pipe(
      map(
        ([
          isFitterSuperAdmin,
          isFitterAdmin,
          hasFitterSearchAcces,
          isManagerWithGroups,
        ]) => {
          if (isFitterAdmin || isFitterSuperAdmin) {
            return true;
          } else return hasFitterSearchAcces && isManagerWithGroups;
        }
      )
    );
  }

  public userIsFitterSuperAdmin$() {
    return this.userIsGroup$('grpFitterSuperAdmin');
  }

  userHasFitterAdminGroupAccess$(fitter: Fitter) {
    return this.userIsFitterAdmin$()?.pipe(
      map((isSuperAdmin) => {
        return (
          isSuperAdmin &&
          fitter?.managers?.findIndex(
            (m) =>
              m?.email === this.getAccountLogin() &&
              m?.accessTypes?.includes('grpFitterAdmin')
          ) > -1
        );
      })
    );
  }

  public userIsFitterAdmin$() {
    return this.userIsGroup$('grpFitterAdmin');
  }

  public userHasInsurerDashboardAccess$() {
    return this.userIsGroup$('grpInsurerDashboard').pipe(
      switchMap((isDashboard) =>
        this.userIsGroup$('grpInsurerB2BRestrictedStatistics').pipe(
          map((isRestricted) => isDashboard || isRestricted)
        )
      )
    );
  }

  public userHasInsurerB2BRestrictedStatistics$() {
    return this.userIsGroup$('grpInsurerB2BRestrictedStatistics');
  }

  public userHasInsurerPartnerFitterAccess$() {
    return this.userIsGroup$('grpInsurerAccessPartnerFitter');
  }

  public userHasClaimsListAccess$() {
    return this.userIsGroup$('grpInsurerAccessClaimsList');
  }

  public userHasInsurerAdminAccess$() {
    return this.userIsGroup$('grpInsurerAdmin');
  }

  userHasFitterDashboardOktaAccess$(acceptAdmin = false) {
    return (
      this.userIsGroup$('grpFitterDashboard') ||
      (acceptAdmin &&
        (this.userIsFitterSuperAdmin$() || this.userIsFitterAdmin$()))
    );
  }

  userHasFitterDashboardAccess$(fitters: Fitter[] = []) {
    return combineLatest([
      this.userIsFitterSuperAdmin$(),
      this.userIsFitterAdmin$(),
      this.userHasFitterDashboardOktaAccess$(),
      this.userIsManagerWithGroup$(fitters, 'grpFitterDashboard', true),
    ]).pipe(
      map(
        ([
          isFitterSuperAdmin,
          isFitterAdmin,
          hasFitterDashboardAcces,
          isManagerWithGroups,
        ]) => {
          if (isFitterAdmin || isFitterSuperAdmin) {
            return true;
          } else return hasFitterDashboardAcces && isManagerWithGroups;
        }
      )
    );
  }

  userIsManagerWithGroup$(
    fitters: Fitter[],
    group: FitterAccessType,
    acceptAdmin = false
  ) {
    return combineLatest([
      this.getUserLogin$(),
      this.userIsFitterSuperAdmin$(),
    ]).pipe(
      map(([login, isAdmin]) => {
        return (
          fitters.findIndex(
            (f) =>
              f.managers?.findIndex(
                (m) => m.email === login && m.accessTypes?.includes(group)
              ) > -1
          ) > -1 ||
          (acceptAdmin && isAdmin)
        );
      })
    );
  }
  //#endregion

  //#region Dossier Infos
  public getCurrentDossierId() {
    return this.storage.getItem('dossier-number', sessionStorage);
  }

  public setCurrentDossierId(dossierId: string) {
    return this.storage.setItem('dossier-number', dossierId, sessionStorage);
  }

  public clearCurrentDossierId() {
    return this.storage.removeItem('dossier-number', sessionStorage);
  }

  public isDossierAccess() {
    return this.storage.getItem('is-dossier-access', sessionStorage);
  }

  public setDossierAccess(isDossierAccess: boolean) {
    return this.storage.setItem(
      'is-dossier-access',
      isDossierAccess,
      sessionStorage
    );
  }

  removeDossierAccess() {
    this.storage.removeItem('is-dossier-access', sessionStorage);
  }

  setAccountLogin(login: string) {
    this.storage.setItem('account-login', login, sessionStorage);
  }

  getAccountLogin(): string {
    return this.storage.getItem('account-login', sessionStorage);
  }

  setAccountFirstName(firstName: string): void {
    this.storage.setItem('account-first-name', firstName, sessionStorage);
  }

  getAccountFirstName(): string {
    return this.storage.getItem('account-first-name', sessionStorage);
  }

  setAccountLastName(lastName: string): void {
    this.storage.setItem('account-last-name', lastName, sessionStorage);
  }

  getAccountLastName(): string {
    return this.storage.getItem('account-last-name', sessionStorage);
  }

  // Tests if the current user is the client of the current dossier
  public isCurrentDossierAuth$() {
    return this.getUserLogin$().pipe(
      map(
        (userLogin) => userLogin?.split('.')[0] === this.getCurrentDossierId()
      )
    );
  }

  public setHoldAction(action: CtaAction) {
    this.storage.setItem('action', action, sessionStorage);
  }

  public getAndClearHoldAction(): CtaAction {
    const action = this.storage.getItem('action', sessionStorage);
    if (action) this.storage.removeItem('action', sessionStorage);
    return action as CtaAction;
  }
  //#endregion
}
